<template>
  <span v-if="show" class="d-inline-flex align-center flex-wrap">
    <v-tooltip bottom>
      <template v-slot:activator="{ on: tooltip }">
        <v-chip small :color="isCurrent ? 'primary' : 'grey darken-1'" text-color="white" v-on="tooltip">
          v{{ versionNumber(doc) }}<span v-if="isCurrent">&nbsp;· {{ $t('components.ModelVersionBadge.currentVersion') }}</span>
          <v-menu offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
              <v-icon small right v-bind="attrs" v-on="menu" :aria-label="$t('components.ModelVersionBadge.selectVersion')">mdi-chevron-down</v-icon>
            </template>
            <v-list dense>
              <v-subheader>{{ $t('components.ModelVersionBadge.selectVersion') }}</v-subheader>
              <v-list-item
                v-for="version in versionsNewestFirst"
                :key="version.id"
                :input-value="version.id === doc.id"
                color="primary"
                @click="selectVersion(version)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    v{{ versionNumber(version) }}<span v-if="version.id === latest.id">&nbsp;· {{ $t('components.ModelVersionBadge.currentVersion') }}</span>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="deployDate(version)">{{ $t('components.ModelVersionBadge.versionDeployed', { date: deployDate(version) }) }}</v-list-item-subtitle>
                  <v-list-item-subtitle v-if="version.version_notes">{{ version.version_notes }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-chip>
      </template>
      <div v-if="deployDate(doc)">{{ $t('components.ModelVersionBadge.versionDeployed', { date: deployDate(doc) }) }}</div>
      <div v-if="doc.version_notes">{{ doc.version_notes }}</div>
    </v-tooltip>
    <v-btn v-if="goToCurrent && !isCurrent" x-small text color="primary" class="px-1" @click="selectVersion(latest)">
      {{ $t('components.ModelVersionBadge.goToCurrentVersion', { version: versionNumber(latest) }) }}
    </v-btn>
  </span>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { latestInFamily, versionFamilyId, versionNumber } from '@/utils/modelVersions.js'

// "vN · current version" chip for an Ad-Hoc model that belongs to a family with more than one version. Its
// arrow opens a menu to switch to another version of the family. With go-to-current, an older version also
// gets a button that switches to the current version.
export default {
  props: {
    model: {
      type: Object,
      default: null
    },
    goToCurrent: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters('clients', [
      'modelVersionFamilies'
    ]),
    versions () {
      if (!this.model || this.model.type !== 'mmm') {
        return []
      }
      return this.modelVersionFamilies.get(versionFamilyId(this.model)) || []
    },
    versionsNewestFirst () {
      return this.versions.slice().reverse()
    },
    // The model as it is now in the family: callers may pass a copy taken earlier (currentClientObject).
    doc () {
      return this.versions.find(version => version.id === this.model.id) || this.model
    },
    show () {
      return this.versions.length > 1
    },
    latest () {
      return latestInFamily(this.versions)
    },
    isCurrent () {
      return this.latest.id === this.doc.id
    }
  },
  methods: {
    ...mapActions('clients', [
      'setCurrentClient'
    ]),
    versionNumber,
    // date_created: in production importModel creates the doc on deploy, so it is the deploy date.
    deployDate (version) {
      return version.date_created ? moment(version.date_created.toDate()).format('YYYY/MM/DD HH:mm') : ''
    },
    // Same switch as selecting a model in App.vue (currentClientLocal's setter): a plan belongs to one
    // version, so from a plan it goes to the Model Report; any other page stays where it is.
    selectVersion (version) {
      if (version.id === this.doc.id) {
        return
      }
      if (this.$route.name === 'simulationDetails') {
        this.$router.push({ name: 'modelReport' }).catch(err => {
          if (err && err.name !== 'NavigationDuplicated') {
            console.log(err)
          }
        })
      }
      this.setCurrentClient(version.id)
    }
  }
}
</script>
